
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Zap, Plus, X, Dumbbell, Utensils, Footprints, Heart, Wind, Check, Loader2 } from 'lucide-react';
import { logActivity } from '../services/trackingService';
import { User } from '../types';

export const QuickLogFAB = ({ user }: { user: User }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [loadingId, setLoadingId] = useState<string | null>(null);
  const [successId, setSuccessId] = useState<string | null>(null);

  const activities = [
    { id: 'gym', labelKey: 'activities.workout', icon: <Dumbbell size={20}/>, color: 'bg-emerald-500', shadow: 'shadow-emerald-500/40', xp: 50 },
    { id: 'diet', labelKey: 'activities.diet', icon: <Utensils size={20}/>, color: 'bg-orange-500', shadow: 'shadow-orange-500/40', xp: 30 },
    { id: 'cardio', labelKey: 'activities.cardio', icon: <Heart size={20}/>, color: 'bg-red-500', shadow: 'shadow-red-500/40', xp: 40 },
    { id: 'yoga', labelKey: 'activities.yoga', icon: <Wind size={20}/>, color: 'bg-indigo-500', shadow: 'shadow-indigo-500/40', xp: 35 },
  ];

  const handleQuickLog = async (type: string) => {
    setLoadingId(type);
    try {
      const result = await logActivity(user, type as any, true);
      if (result.status === 'success' || result.status === 'merged') {
        setSuccessId(type);
        // Reset success state and close after a short delay
        setTimeout(() => {
          setSuccessId(null);
          setIsOpen(false);
        }, 1000);
      } else {
        console.warn("Activity logging returned status:", result.status);
        // Still show success to user even if there was a minor issue
        setSuccessId(type);
        setTimeout(() => {
          setSuccessId(null);
          setIsOpen(false);
        }, 1000);
      }
    } catch (error: any) {
      // Error is already logged in trackingService
      // Don't show error to user - just reset loading state
      console.warn("Activity logging failed:", error?.message || error);
    } finally {
      setLoadingId(null);
    }
  };

  return (
    <div className="fixed bottom-24 right-6 z-[100] flex flex-col items-end">
      {/* Menu espandibile */}
      <div className={`flex flex-col gap-4 mb-4 transition-all duration-300 transform ${
        isOpen ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-50 pointer-events-none'
      }`}>
        {activities.map((act) => (
          <div key={act.id} className="flex items-center gap-3 group">
            <span className="bg-black/80 backdrop-blur-md border border-white/10 px-3 py-1.5 rounded-xl text-[10px] font-black text-white shadow-2xl opacity-0 group-hover:opacity-100 transition-opacity uppercase tracking-widest whitespace-nowrap">
              {t(act.labelKey)} <span className="text-emerald-400">+{act.xp} XP</span>
            </span>
            <button
              onClick={() => handleQuickLog(act.id)}
              disabled={loadingId !== null}
              className={`w-14 h-14 rounded-2xl flex items-center justify-center text-white transition-all shadow-lg ${act.color} ${act.shadow} hover:scale-110 active:scale-90`}
            >
              {loadingId === act.id ? (
                <Loader2 size={24} className="animate-spin" />
              ) : successId === act.id ? (
                <Check size={24} className="animate-bounce" />
              ) : (
                act.icon
              )}
            </button>
          </div>
        ))}
      </div>

      {/* Pulsante Principale (Trigger) */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`w-16 h-16 rounded-[2rem] flex items-center justify-center text-white shadow-2xl transition-all duration-500 transform border-4 border-black/20 ${
          isOpen 
            ? 'bg-slate-800 rotate-45 text-white' 
            : 'bg-gradient-to-tr from-cyan-400 to-blue-600 hover:scale-110 hover:shadow-cyan-500/50'
        }`}
      >
        {isOpen ? <X size={32} className="text-white" /> : <Zap size={32} fill="currentColor" className="drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]" />}
        
        {/* Badge di notifica (opzionale, indica che c'è qualcosa da loggare) */}
        {!isOpen && (() => {
          const now = new Date();
          const currentCalendar = (user.monthlyCalendars || []).find(
            cal => cal.year === now.getFullYear() && cal.month === now.getMonth() + 1 && !cal.archived
          );
          return !currentCalendar || currentCalendar.totalStamps === 0;
        })() && (
          <div className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full border-4 border-black flex items-center justify-center">
            <div className="w-1.5 h-1.5 bg-white rounded-full animate-ping" />
          </div>
        )}
      </button>
    </div>
  );
};
